import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FoldersTabs } from "./components/foldersTabs";
import { FlexBox, useBreakpoint, useToasts } from "@brg/ui";
import { DesignerTagsCloud } from "./components/designerTagsCloud";
import { useDispatch, useSelector } from "react-redux";
import ProfileFoldersSlice, { ProfileFoldersProps, ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice";
import { FolderEdit } from "./components/FolderEdit";
import ProjectsFolderController from "core/controllers/ProjectsFolderController";
import useQueryState from "shared/hooks/useQueryState";
import { useFoldersController } from "shared/hooks/useFoldersController";
import { FolderOptions } from "./components/folderOptions";
import { FOLDERS_MODE, FolderT } from "../tabs/const";
import { FolderEmptyOptions } from "./components/folderEmptyOptions";
import styled from "styled-components";
import { LayoutConst, useModal } from "core";
import { fetchFolders } from "shared/actions/folderProjectsActions";
import { useTranslation } from "react-i18next";

export const MAX_FOLDERS_COUNT = 10;
export const MAX_FOLDERS_PROJECTS_COUNT = 32;
export const PROJECTS_COLUMNS_DESKTOP_COUNT = 4;
export const PROJECTS_COLUMNS_MOB_COUNT = 2;

export const FoldersContainer = styled(FlexBox)`
    width: 100%;
    max-width: 1152px;
`;

interface FoldersProps {
    tabName: string, 
    designerId:string, 
    filterList: any, 
    tagId: string,
    isMe?: boolean,
    setTagId: (ev: any) => void, 
}


const DesignerFolders: FC<FoldersProps> = ({
    designerId,
    tabName = "", 
    filterList = null, 
    tagId, 
    setTagId,
    isMe 
}) => {
    const { currentFolder, foldersList, isLoading } = useSelector(ProfileFoldersSelector);
    const [time, setTime] = useState(Date.now());
    const {info,error} = useToasts();
    const { t } = useTranslation();
    const { onDelete, onEdit, onMoveProject, onAddProject, onRemoveProject } = useFoldersController();
    const data = useSelector(ProfileFoldersSelector)?.data
    const fetchingTabs = useSelector(ProfileFoldersSelector)?.isLoading;
    const [{ folderId }, setQueryParams ] = useQueryState();
    const dispatch = useDispatch();
    const modal = useModal();
    const isMobile = useBreakpoint(LayoutConst.screen.s -1);
    const contentIsLoading = false;

    const defaultFolder = useMemo(
        () => (foldersList?.length > 0 ? foldersList?.find((i) => !!i?.isDefault) : undefined
    ) , [foldersList])

    const activeFolder = useMemo(() => {
        if (!folderId || !foldersList?.length) return undefined;

        return foldersList?.find(({ id }) => id === folderId) || undefined
    } , [foldersList, folderId, time])

    const projectIds = useMemo(() => {
        if (folderId && !!foldersList?.length) {
            const folder = foldersList?.find(({ id }) => id === folderId)

            return folder?.entityIds?.filter((i) => i !== '') || [];
        }
    } , [foldersList, folderId, isLoading])

    const folderFilter = useMemo(() => {
        if (!projectIds?.length) return undefined
        return {
            projectIds,
            isContextIgnored: true,
            OrderByProjectIds: true,
            limit: MAX_FOLDERS_PROJECTS_COUNT
        }
    } , [projectIds, isLoading])

    const isEmptyFolder = useMemo(() => {
        if (isLoading) return false
        return (!activeFolder?.entityIds || activeFolder?.entityIds?.length === 0) && folderId
    }, [activeFolder, folderId, isLoading])

    const onFolderChange = (s:ProfileFoldersProps, folderId?: string) => {
        if (folderId) {
            setQueryParams({ folderId })
        }

        dispatch(ProfileFoldersSlice.actions.setCurrentFolder(s))
    }

    const getGridColumnsCount = () => {
        return isMobile ? PROJECTS_COLUMNS_MOB_COUNT : PROJECTS_COLUMNS_DESKTOP_COUNT;
    }

    const onDragProjects = useCallback((arr: any) => {
        const sortableIds: any = [];

        if (!isMe || !arr?.length) return null;

        arr.forEach((lo: any) => {
            sortableIds.push({
                id: lo.i,
                order: (lo.y) * getGridColumnsCount() + lo.x 
              })
            return {
              id: lo.i,
              order: (lo.y)*4+lo.x 
            }
          })
          sortableIds.sort((a: any,b: any) => {
            return a.order > b.order ? 1 : -1;
          });

        let list = [...foldersList]
        const ids = sortableIds?.map((item: any) => item?.id);
        const newFolder = {...activeFolder, entityIds: ids };

        // const isEqual = JSON.stringify(ids) === JSON.stringify(activeFolder?.entityIds)

        // if (arr?.length) return null;

        // console.log('tst arr', arr)

        onMoveProject(newFolder as FolderT)

        const idx = foldersList?.findIndex((folder) => folder?.id === newFolder?.id)
        //@ts-ignore
        list[idx] = newFolder
        dispatch(ProfileFoldersSlice.actions.setFolders(list))
    } , [activeFolder, foldersList, isMe])

    const onUpdateFolders = (t: number) => {
        setTimeout(() => {
            setTime(t)
        } , 500)
    }

    const onEditToggle = useCallback(() => {
        if (currentFolder === ProfileFoldersProps.edit) {
            onFolderChange(ProfileFoldersProps.projects, folderId)
            return;
        }
        onFolderChange(ProfileFoldersProps.edit, folderId)
    } , [folderId])

    const onPinToggle = useCallback(() => {
        const otherDefaultList = foldersList?.filter((i) => i?.id !== activeFolder?.id && !!i?.isDefault)

        if (otherDefaultList?.length > 0) {
            Promise.all(otherDefaultList?.map(async(folder) => {
                onEdit && onEdit({
                    ...folder, 
                    isDefault: false 
                }
                , FOLDERS_MODE.edit,
                true
            )
            }))
        }

        if (activeFolder) {
            onEdit && onEdit({
                    ...activeFolder, 
                    isDefault: !activeFolder?.isDefault,
                    ...(!activeFolder?.isDefault === true && { isHidden: false })
                }
                , FOLDERS_MODE.edit,
                true
            ).then((r) => {
                info(t('folders.modal.success_edit', { name: activeFolder.name }))
                onUpdateFolders(Date.now())
            })
        }
    } , [foldersList, activeFolder])
  
    const onHiddenToggle = useCallback(() => {
        if (activeFolder) {
            onEdit && onEdit({
                    ...activeFolder, 
                    isHidden: !activeFolder?.isHidden,
                    ...(!activeFolder?.isHidden === true && { isDefault: false }) 
                }
                , FOLDERS_MODE.edit,
                true
            ).then((r) => {
                info(t('folders.modal.success_edit', { name: activeFolder.name }))
                onUpdateFolders(Date.now())
            })
        }
    } , [activeFolder])

    const onChooseProject = useCallback((folder: FolderT) => {

        const newIds = folder?.entityIds;

        const newList = foldersList?.map((fl) => {
            if (fl?.id === folder?.id) {
                return {...fl, entityIds: newIds}
            }
            return {
                ...fl,
                entityIds: fl?.entityIds?.filter((i) => !newIds?.includes(i))
            }
        })

        Promise.all(newList?.map(async(folder) => {
            onEdit(folder, FOLDERS_MODE.edit, true);
        })).then((r) => {
            modal.hide();
            info(t('folders.modal.success_edit', { name: folder.name }));
            onUpdateFolders(Date.now())
        })

    } , [activeFolder, foldersList])

    const onAddModal = useCallback(() => {
        modal.show('folder-projects-edit', {
            folder: activeFolder,
            onSubmit: onChooseProject,
        })
    } , [activeFolder, onAddProject, onRemoveProject, onChooseProject])
    

    const onDeleteFolder = useCallback(() => {
        activeFolder && onDelete && onDelete(activeFolder?.id ?? '', true)?.then((r) => {
            info(t('folders.modal.success_delete_folder', { name: activeFolder.name }));
            onUpdateFolders(Date.now())
        })
        if (!!defaultFolder) {
            onFolderChange(ProfileFoldersProps.projects, defaultFolder?.id)
        } else {
            onFolderChange(ProfileFoldersProps.projects)
            setQueryParams({ folderId: undefined })
        }
    } , [activeFolder, onDelete])

    const tabs = useMemo(() => {
        return (
            <FoldersTabs 
                designerId={designerId}
                setTagId={setTagId} 
                tabs={foldersList}
                update={time}
                activeId={activeFolder?.id}
                isMe={isMe} 
            />
        )
    } , [designerId, currentFolder, isMe, activeFolder, foldersList, time])

    const options = useMemo(() => {
        if (!isMe || ![ProfileFoldersProps.projects , ProfileFoldersProps.edit]?.includes(currentFolder) || !activeFolder) return null;

        return (
            <FolderOptions
                currentFolder={currentFolder}
                activeFolder={activeFolder}
                onEdit={onEditToggle}
                onHidden={onHiddenToggle}
                onPin={onPinToggle}
                onAdd={onAddModal}
                isEmptyFolder={isEmptyFolder} 
            />
        )
    } , [designerId, isMe, currentFolder, isEmptyFolder, activeFolder, time])

    const edit = useMemo(() => {
        if (![ProfileFoldersProps.create, ProfileFoldersProps.edit]?.includes(currentFolder)) return null;
        return (
            <FolderEdit
                activeFolder={activeFolder} 
                folderId={folderId}
                // activeFolder={activeFolder} 
                callback={(t: any) => {
                    onUpdateFolders(t)
                }} 
            />
        )
    } , [currentFolder, folderId, activeFolder])

    const cloud = useMemo(() => {
        return (
            <DesignerTagsCloud 
                designerId={designerId || ""}
                // folderId={activeFolder?.id}
                folders={foldersList} 
                tabName={tabName}
                update={time} 
                filterList={filterList} 
                tagId={tagId} 
                setTagId={setTagId} 
                // visible={currentFolder === ProfileFoldersProps.cloud || currentFolder === ProfileFoldersProps.projects}
                visible={tabName === 'projects'}
            />
        )
    } , [
        currentFolder,
        designerId,
        time,
        foldersList,
        activeFolder,
        tabName,
        filterList,
        tagId
    ])

    const content = useMemo(() => {
        if (currentFolder !== ProfileFoldersProps.projects || !folderId) return null;

        if (isEmptyFolder) {
            return (
                <FolderEmptyOptions 
                    isMe={isMe || false}
                    activeFolder={activeFolder}
                    onAdd={onAddModal}
                    onDelete={onDeleteFolder}
                />
            )
        }
        return (
            <ProjectsFolderController 
                filter={folderFilter}
                onDragProjects={onDragProjects}
                folderId={folderId}
                loading={contentIsLoading}
                canDrag={isMe}
                isMobileMoreVisible
                isMainPage
                isProfile
                // @ts-ignore
                isMe={isMe}
            />
        )
    } , [folderId, contentIsLoading, currentFolder, folderFilter, isMe, activeFolder, isEmptyFolder])

    useEffect(() => {
        if (folderId && currentFolder === ProfileFoldersProps.cloud) {
            dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
        }
    } , [folderId, currentFolder])

    useEffect(() => {
        dispatch(fetchFolders({ designerId }));
    } , [time, designerId])

    if (tabName !== 'projects') {
        return null;
    }

    return (
        <FoldersContainer direction="column">
            {/* {tabs} */}
            {cloud}
            {options}
            {edit}
            {content}
        </FoldersContainer>
    )
}

export default DesignerFolders;import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FoldersTabs } from "./components/foldersTabs";
import { FlexBox, useBreakpoint, useToasts } from "@brg/ui";
import { DesignerTagsCloud } from "./components/designerTagsCloud";
import { useDispatch, useSelector } from "react-redux";
import ProfileFoldersSlice, { ProfileFoldersProps, ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice";
import { FolderEdit } from "./components/FolderEdit";
import ProjectsFolderController from "core/controllers/ProjectsFolderController";
import useQueryState from "shared/hooks/useQueryState";
import { useFoldersController } from "shared/hooks/useFoldersController";
import { FolderOptions } from "./components/folderOptions";
import { FOLDERS_MODE, FolderT } from "../tabs/const";
import { FolderEmptyOptions } from "./components/folderEmptyOptions";
import styled from "styled-components";
import { LayoutConst, useModal } from "core";
import { fetchFolders } from "shared/actions/folderProjectsActions";
import { useTranslation } from "react-i18next";

export const MAX_FOLDERS_COUNT = 10;
export const MAX_FOLDERS_PROJECTS_COUNT = 32;
export const PROJECTS_COLUMNS_DESKTOP_COUNT = 4;
export const PROJECTS_COLUMNS_MOB_COUNT = 2;

export const FoldersContainer = styled(FlexBox)`
    width: 100%;
    max-width: 1152px;
`;

interface FoldersProps {
    tabName: string, 
    designerId:string, 
    filterList: any, 
    tagId: string,
    isMe?: boolean,
    setTagId: (ev: any) => void, 
}


const DesignerFolders: FC<FoldersProps> = ({
    designerId,
    tabName = "", 
    filterList = null, 
    tagId, 
    setTagId,
    isMe 
}) => {
    const { currentFolder, foldersList, isLoading } = useSelector(ProfileFoldersSelector);
    const [time, setTime] = useState(Date.now());
    const {info,error} = useToasts();
    const { t } = useTranslation();
    const { onDelete, onEdit, onMoveProject, onAddProject, onRemoveProject } = useFoldersController();
    const data = useSelector(ProfileFoldersSelector)?.data
    const fetchingTabs = useSelector(ProfileFoldersSelector)?.isLoading;
    const [{ folderId }, setQueryParams ] = useQueryState();
    const dispatch = useDispatch();
    const modal = useModal();
    const isMobile = useBreakpoint(LayoutConst.screen.s -1);
    const contentIsLoading = false;

    const defaultFolder = useMemo(
        () => (foldersList?.length > 0 ? foldersList?.find((i) => !!i?.isDefault) : undefined
    ) , [foldersList])

    const activeFolder = useMemo(() => {
        if (!folderId || !foldersList?.length) return undefined;

        return foldersList?.find(({ id }) => id === folderId) || undefined
    } , [foldersList, folderId, time])

    const projectIds = useMemo(() => {
        if (folderId && !!foldersList?.length) {
            const folder = foldersList?.find(({ id }) => id === folderId)

            return folder?.entityIds?.filter((i) => i !== '') || [];
        }
    } , [foldersList, folderId, isLoading])

    const folderFilter = useMemo(() => {
        if (!projectIds?.length) return undefined
        return {
            projectIds,
            isContextIgnored: true,
            OrderByProjectIds: true,
            limit: MAX_FOLDERS_PROJECTS_COUNT
        }
    } , [projectIds, isLoading])

    const isEmptyFolder = useMemo(() => {
        if (isLoading) return false
        return (!activeFolder?.entityIds || activeFolder?.entityIds?.length === 0) && folderId
    }, [activeFolder, folderId, isLoading])

    const onFolderChange = (s:ProfileFoldersProps, folderId?: string) => {
        if (folderId) {
            setQueryParams({ folderId })
        }

        dispatch(ProfileFoldersSlice.actions.setCurrentFolder(s))
    }

    const getGridColumnsCount = () => {
        return isMobile ? PROJECTS_COLUMNS_MOB_COUNT : PROJECTS_COLUMNS_DESKTOP_COUNT;
    }

    const onDragProjects = useCallback((arr: any) => {
        const sortableIds: any = [];

        if (!isMe || !arr?.length) return null;

        arr.forEach((lo: any) => {
            sortableIds.push({
                id: lo.i,
                order: (lo.y) * getGridColumnsCount() + lo.x 
              })
            return {
              id: lo.i,
              order: (lo.y)*4+lo.x 
            }
          })
          sortableIds.sort((a: any,b: any) => {
            return a.order > b.order ? 1 : -1;
          });

        let list = [...foldersList]
        const ids = sortableIds?.map((item: any) => item?.id);
        const newFolder = {...activeFolder, entityIds: ids };

        // const isEqual = JSON.stringify(ids) === JSON.stringify(activeFolder?.entityIds)

        // if (arr?.length) return null;

        // console.log('tst arr', arr)

        onMoveProject(newFolder as FolderT)

        const idx = foldersList?.findIndex((folder) => folder?.id === newFolder?.id)
        //@ts-ignore
        list[idx] = newFolder
        dispatch(ProfileFoldersSlice.actions.setFolders(list))
    } , [activeFolder, foldersList, isMe])

    const onUpdateFolders = (t: number) => {
        setTimeout(() => {
            setTime(t)
        } , 500)
    }

    const onEditToggle = useCallback(() => {
        if (currentFolder === ProfileFoldersProps.edit) {
            onFolderChange(ProfileFoldersProps.projects, folderId)
            return;
        }
        onFolderChange(ProfileFoldersProps.edit, folderId)
    } , [folderId])

    const onPinToggle = useCallback(() => {
        const otherDefaultList = foldersList?.filter((i) => i?.id !== activeFolder?.id && !!i?.isDefault)

        if (otherDefaultList?.length > 0) {
            Promise.all(otherDefaultList?.map(async(folder) => {
                onEdit && onEdit({
                    ...folder, 
                    isDefault: false 
                }
                , FOLDERS_MODE.edit,
                true
            )
            }))
        }

        if (activeFolder) {
            onEdit && onEdit({
                    ...activeFolder, 
                    isDefault: !activeFolder?.isDefault,
                    ...(!activeFolder?.isDefault === true && { isHidden: false })
                }
                , FOLDERS_MODE.edit,
                true
            ).then((r) => {
                info(t('folders.modal.success_edit', { name: activeFolder.name }))
                onUpdateFolders(Date.now())
            })
        }
    } , [foldersList, activeFolder])
  
    const onHiddenToggle = useCallback(() => {
        if (activeFolder) {
            onEdit && onEdit({
                    ...activeFolder, 
                    isHidden: !activeFolder?.isHidden,
                    ...(!activeFolder?.isHidden === true && { isDefault: false }) 
                }
                , FOLDERS_MODE.edit,
                true
            ).then((r) => {
                info(t('folders.modal.success_edit', { name: activeFolder.name }))
                onUpdateFolders(Date.now())
            })
        }
    } , [activeFolder])

    const onChooseProject = useCallback((folder: FolderT) => {

        const newIds = folder?.entityIds;

        const newList = foldersList?.map((fl) => {
            if (fl?.id === folder?.id) {
                return {...fl, entityIds: newIds}
            }
            return {
                ...fl,
                entityIds: fl?.entityIds?.filter((i) => !newIds?.includes(i))
            }
        })

        Promise.all(newList?.map(async(folder) => {
            onEdit(folder, FOLDERS_MODE.edit, true);
        })).then((r) => {
            modal.hide();
            info(t('folders.modal.success_edit', { name: folder.name }));
            onUpdateFolders(Date.now())
        })

    } , [activeFolder, foldersList])

    const onAddModal = useCallback(() => {
        modal.show('folder-projects-edit', {
            folder: activeFolder,
            onSubmit: onChooseProject,
        })
    } , [activeFolder, onAddProject, onRemoveProject, onChooseProject])
    

    const onDeleteFolder = useCallback(() => {
        activeFolder && onDelete && onDelete(activeFolder?.id ?? '', true)?.then((r) => {
            info(t('folders.modal.success_delete_folder', { name: activeFolder.name }));
            onUpdateFolders(Date.now())
        })
        if (!!defaultFolder) {
            onFolderChange(ProfileFoldersProps.projects, defaultFolder?.id)
        } else {
            onFolderChange(ProfileFoldersProps.projects)
            setQueryParams({ folderId: undefined })
        }
    } , [activeFolder, onDelete])

    const tabs = useMemo(() => {
        return (
            <FoldersTabs 
                designerId={designerId}
                setTagId={setTagId} 
                tabs={foldersList}
                update={time}
                activeId={activeFolder?.id}
                isMe={isMe} 
            />
        )
    } , [designerId, currentFolder, isMe, activeFolder, foldersList, time])

    const options = useMemo(() => {
        if (!isMe || ![ProfileFoldersProps.projects , ProfileFoldersProps.edit]?.includes(currentFolder) || !activeFolder) return null;

        return (
            <FolderOptions
                currentFolder={currentFolder}
                activeFolder={activeFolder}
                onEdit={onEditToggle}
                onHidden={onHiddenToggle}
                onPin={onPinToggle}
                onAdd={onAddModal}
                isEmptyFolder={isEmptyFolder} 
            />
        )
    } , [designerId, isMe, currentFolder, isEmptyFolder, activeFolder, time])

    const edit = useMemo(() => {
        if (![ProfileFoldersProps.create, ProfileFoldersProps.edit]?.includes(currentFolder)) return null;
        return (
            <FolderEdit
                activeFolder={activeFolder} 
                folderId={folderId}
                // activeFolder={activeFolder} 
                callback={(t: any) => {
                    onUpdateFolders(t)
                }} 
            />
        )
    } , [currentFolder, folderId, activeFolder])

    const cloud = useMemo(() => {
        return (
            <DesignerTagsCloud 
                designerId={designerId || ""}
                // folderId={activeFolder?.id}
                folders={foldersList} 
                tabName={tabName}
                update={time} 
                filterList={filterList} 
                tagId={tagId} 
                setTagId={setTagId} 
                // visible={currentFolder === ProfileFoldersProps.cloud || currentFolder === ProfileFoldersProps.projects}
                visible={tabName === 'projects'}
            />
        )
    } , [
        currentFolder,
        designerId,
        time,
        foldersList,
        activeFolder,
        tabName,
        filterList,
        tagId
    ])

    const content = useMemo(() => {
        if (currentFolder !== ProfileFoldersProps.projects || !folderId) return null;

        if (isEmptyFolder) {
            return (
                <FolderEmptyOptions 
                    isMe={isMe || false}
                    activeFolder={activeFolder}
                    onAdd={onAddModal}
                    onDelete={onDeleteFolder}
                />
            )
        }
        return (
            <ProjectsFolderController 
                filter={folderFilter}
                onDragProjects={onDragProjects}
                folderId={folderId}
                loading={contentIsLoading}
                canDrag={isMe}
                isMobileMoreVisible
                isMainPage
                isProfile
                // @ts-ignore
                isMe={isMe}
            />
        )
    } , [folderId, contentIsLoading, currentFolder, folderFilter, isMe, activeFolder, isEmptyFolder])

    useEffect(() => {
        if (folderId && currentFolder === ProfileFoldersProps.cloud) {
            dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
        }
    } , [folderId, currentFolder])

    useEffect(() => {
        dispatch(fetchFolders({ designerId }));
    } , [time, designerId])

    if (tabName !== 'projects') {
        return null;
    }

    return (
        <FoldersContainer direction="column">
            {/* {tabs} */}
            {cloud}
            {options}
            {edit}
            {content}
        </FoldersContainer>
    )
}

export default DesignerFolders;